import { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable, ActivityIndicator } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { supabase } from '../../lib/supabase';
import { Check, ChevronDown, ChevronUp, Settings } from '../../lib/icons';
import { shadows } from '../../lib/styles';
import {
  DEFAULT_VISIT_DURATION_BY_TYPE,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
  isValidVisitDurationMinutes,
} from '../../lib/visitDuration';
import {
  isMissingVisitDurationDefaultsTableError,
  normalizeVisitDurationDefaults,
} from '../../lib/visitDurationDefaults';
import {
  defaultExpensePaymentRequestBody,
  defaultExpensePaymentRequestSubject,
} from '../../lib/expensePaymentRequests';

type VisitType = 'maintenance' | 'service' | 'walkthrough';

type CalendarDescriptionTemplateRow = {
  id: string;
  visit_type: VisitType;
  body_template: string;
  is_active: boolean;
};

type ExpensePaymentTemplateRow = {
  id: string;
  template_key: string;
  subject_template: string;
  body_template: string;
  is_active: boolean;
};

type VisitDurationDefaultRow = {
  visit_type: VisitType;
  duration_minutes: number;
};

type AdminTaskDefaultTemplate = {
  id: string;
  key: string;
  title: string;
  description: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assigned_to: string | null;
  sort_order: number;
  is_active: boolean;
};

type SystemManagerOption = {
  id: string;
  full_name: string | null;
};

type MarketOption = {
  id: string;
  name: string;
};

type BillingProductDraft = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  kind: 'base_plan' | 'addon';
  billing_mode: 'subscription' | 'payment';
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  stripe_metered_or_extra_price_id: string | null;
  plan_value: string | null;
  amount_cents: number;
  currency: string;
  interval: string | null;
  included_units: number | null;
  additional_unit_amount_cents: number | null;
  is_active: boolean;
  sort_order: number;
  metadata: Record<string, any>;
  market_ids: string[];
};

const VISIT_TYPES: VisitType[] = ['maintenance', 'service', 'walkthrough'];
const EXPENSE_PAYMENT_TEMPLATE_KEY = 'homeowner_expense_payment_request';

const defaultBodyTemplate = (visitType: VisitType) =>
  `Your HoneyDone ${visitType} visit is scheduled.\n\nPlease visit {{appUrl}} to see visit details and updates.`;

const isMissingAdminTaskDefaultTemplatesTableError = (err: any): boolean => {
  if (!err) return false;
  const code = typeof err === 'object' && err !== null ? err.code : null;
  const message = typeof err === 'object' && err !== null ? err.message : '';
  if (code === '42P01') return true;
  if (typeof message === 'string' && message.includes('admin_task_default_templates')) {
    return /relation .* does not exist|undefined_table/i.test(message);
  }
  return false;
};

const isAdminOnlyBillingProduct = (product: BillingProductDraft) =>
  product.metadata?.admin_only === true || product.metadata?.admin_only === 'true';

const sortBillingProducts = (products: BillingProductDraft[]) =>
  [...products].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

const isMissingBillingProductsTableError = (err: any): boolean => {
  if (!err) return false;
  const code = typeof err === 'object' && err !== null ? err.code : null;
  const message = typeof err === 'object' && err !== null ? err.message : '';
  if (code === '42P01') return true;
  if (typeof message === 'string' && /billing_products|billing_product_markets/i.test(message)) {
    return /relation .* does not exist|undefined_table/i.test(message);
  }
  return false;
};

const ConfigurationTab = () => {
  const [loading, setLoading] = useState(true);
  const [savingVisitType, setSavingVisitType] = useState<VisitType | null>(null);
  const [savingExpenseTemplate, setSavingExpenseTemplate] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);
  const [savingVisitDurationDefaults, setSavingVisitDurationDefaults] = useState(false);
  const [savingBillingProductId, setSavingBillingProductId] = useState<string | null>(null);
  const [visitCalendarInvitesEnabled, setVisitCalendarInvitesEnabled] = useState(true);
  const [visitDurationDefaults, setVisitDurationDefaults] = useState<Record<VisitType, number>>({
    ...DEFAULT_VISIT_DURATION_BY_TYPE,
  });

  // All sections collapsed by default.
  const [emailSectionExpanded, setEmailSectionExpanded] = useState(false);
  const [calendarSettingsExpanded, setCalendarSettingsExpanded] = useState(false);
  const [calendarTemplatesExpanded, setCalendarTemplatesExpanded] = useState(false);
  const [defaultTasksExpanded, setDefaultTasksExpanded] = useState(false);
  const [billingProductsExpanded, setBillingProductsExpanded] = useState(false);
  const [expandedBillingProductIds, setExpandedBillingProductIds] = useState<Record<string, boolean>>({});

  const [defaultTaskTemplates, setDefaultTaskTemplates] = useState<AdminTaskDefaultTemplate[]>([]);
  const [systemManagerOptions, setSystemManagerOptions] = useState<SystemManagerOption[]>([]);
  const [billingProducts, setBillingProducts] = useState<BillingProductDraft[]>([]);
  const [markets, setMarkets] = useState<MarketOption[]>([]);
  const [savingDefaultTaskId, setSavingDefaultTaskId] = useState<string | null>(null);

  const [drafts, setDrafts] = useState<Record<VisitType, { body_template: string }>>({
    maintenance: { body_template: defaultBodyTemplate('maintenance') },
    service: { body_template: defaultBodyTemplate('service') },
    walkthrough: { body_template: defaultBodyTemplate('walkthrough') },
  });
  const [expensePaymentTemplateDraft, setExpensePaymentTemplateDraft] = useState({
    subject_template: defaultExpensePaymentRequestSubject,
    body_template: defaultExpensePaymentRequestBody,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadConfigurationData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: templateData, error: templateError },
        { data: flagsData, error: flagsError },
        { data: expenseTemplateData, error: expenseTemplateError },
        { data: durationDefaultsData, error: durationDefaultsError },
        { data: defaultTaskTemplateData, error: defaultTaskTemplateError },
        { data: systemManagerData, error: systemManagerError },
        { data: billingProductData, error: billingProductError },
        { data: marketData, error: marketError },
        { data: productMarketData, error: productMarketError },
      ] =
        await Promise.all([
          supabase
            .from('visit_calendar_description_templates')
            .select('id, visit_type, body_template, is_active')
            .in('visit_type', VISIT_TYPES)
            .order('visit_type', { ascending: true }),
          supabase
            .from('app_feature_flags')
            .select('is_enabled')
            .eq('key', 'visit_calendar_invites_enabled')
            .maybeSingle(),
          supabase
            .from('expense_payment_email_templates')
            .select('id, template_key, subject_template, body_template, is_active')
            .eq('template_key', EXPENSE_PAYMENT_TEMPLATE_KEY)
            .maybeSingle(),
          supabase
            .from('visit_duration_defaults')
            .select('visit_type, duration_minutes')
            .in('visit_type', VISIT_TYPES)
            .order('visit_type', { ascending: true }),
          supabase
            .from('admin_task_default_templates')
            .select('id, key, title, description, priority, assigned_to, sort_order, is_active')
            .order('sort_order', { ascending: true }),
          supabase
            .from('profiles')
            .select('id, full_name')
            .eq('is_system_manager', true)
            .order('full_name', { ascending: true }),
          supabase
            .from('billing_products')
            .select(`
              id,
              slug,
              name,
              description,
              kind,
              billing_mode,
              stripe_product_id,
              stripe_price_id,
              stripe_metered_or_extra_price_id,
              plan_value,
              amount_cents,
              currency,
              interval,
              included_units,
              additional_unit_amount_cents,
              is_active,
              sort_order,
              metadata
            `)
            .order('sort_order', { ascending: true }),
          supabase
            .from('markets')
            .select('id, name')
            .eq('is_active', true)
            .order('name', { ascending: true }),
          supabase
            .from('billing_product_markets')
            .select('billing_product_id, market_id, is_available')
            .eq('is_available', true),
        ]);

      if (templateError) throw templateError;
      if (flagsError) throw flagsError;
      if (expenseTemplateError) throw expenseTemplateError;
      if (durationDefaultsError && !isMissingVisitDurationDefaultsTableError(durationDefaultsError)) {
        throw durationDefaultsError;
      }
      if (defaultTaskTemplateError && !isMissingAdminTaskDefaultTemplatesTableError(defaultTaskTemplateError)) {
        throw defaultTaskTemplateError;
      }
      if (systemManagerError) throw systemManagerError;
      if (billingProductError && !isMissingBillingProductsTableError(billingProductError)) {
        throw billingProductError;
      }
      if (marketError) throw marketError;
      if (productMarketError && !isMissingBillingProductsTableError(productMarketError)) {
        throw productMarketError;
      }

      const nextDrafts: Record<VisitType, { body_template: string }> = {
        maintenance: { body_template: defaultBodyTemplate('maintenance') },
        service: { body_template: defaultBodyTemplate('service') },
        walkthrough: { body_template: defaultBodyTemplate('walkthrough') },
      };

      (templateData || []).forEach((row) => {
        const typedRow = row as CalendarDescriptionTemplateRow;
        nextDrafts[typedRow.visit_type] = {
          body_template: typedRow.body_template || defaultBodyTemplate(typedRow.visit_type),
        };
      });

      setDrafts(nextDrafts);
      setVisitCalendarInvitesEnabled(flagsData?.is_enabled ?? true);
      const typedExpenseTemplate = expenseTemplateData as ExpensePaymentTemplateRow | null;
      setExpensePaymentTemplateDraft({
        subject_template:
          typedExpenseTemplate?.subject_template?.trim() || defaultExpensePaymentRequestSubject,
        body_template:
          typedExpenseTemplate?.body_template?.trim() || defaultExpensePaymentRequestBody,
      });
      setVisitDurationDefaults(
        normalizeVisitDurationDefaults(
          isMissingVisitDurationDefaultsTableError(durationDefaultsError)
            ? null
            : (durationDefaultsData as VisitDurationDefaultRow[] | null),
        ),
      );
      setDefaultTaskTemplates(
        isMissingAdminTaskDefaultTemplatesTableError(defaultTaskTemplateError)
          ? []
          : ((defaultTaskTemplateData as AdminTaskDefaultTemplate[] | null) || []),
      );
      setSystemManagerOptions((systemManagerData as SystemManagerOption[] | null) || []);
      setMarkets((marketData as MarketOption[] | null) || []);

      if (
        isMissingBillingProductsTableError(billingProductError) ||
        isMissingBillingProductsTableError(productMarketError)
      ) {
        setBillingProducts([]);
      } else {
        const marketIdsByProductId = new Map<string, string[]>();
        ((productMarketData as any[] | null) || []).forEach((row) => {
          if (!row.billing_product_id || !row.market_id) return;
          const current = marketIdsByProductId.get(row.billing_product_id) || [];
          current.push(row.market_id);
          marketIdsByProductId.set(row.billing_product_id, current);
        });

        setBillingProducts(
          ((billingProductData as any[] | null) || []).map((product) => ({
            ...product,
            metadata: product.metadata || {},
            market_ids: marketIdsByProductId.get(product.id) || [],
          })),
        );
      }
    } catch (err: any) {
      console.error('Error loading configuration:', err);
      setError(err?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVisitDurationDefaults = async () => {
    setSavingVisitDurationDefaults(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;
      const payload = VISIT_TYPES.map((visitType) => {
        const draftDuration = visitDurationDefaults[visitType];
        return {
          visit_type: visitType,
          duration_minutes: isValidVisitDurationMinutes(draftDuration)
            ? draftDuration
            : DEFAULT_VISIT_DURATION_BY_TYPE[visitType],
          updated_by: userId,
        };
      });

      const { error: upsertError } = await supabase
        .from('visit_duration_defaults')
        .upsert(payload, { onConflict: 'visit_type' });

      if (upsertError) throw upsertError;

      await loadConfigurationData();
      setSuccess('Visit default durations saved.');
    } catch (err: any) {
      console.error('Error saving visit duration defaults:', err);
      if (isMissingVisitDurationDefaultsTableError(err)) {
        setError(
          'Visit duration defaults are not available yet. Run the latest database migrations and try again.',
        );
      } else {
        setError(err?.message || 'Failed to save visit default durations');
      }
    } finally {
      setSavingVisitDurationDefaults(false);
    }
  };

  const handleSaveExpensePaymentTemplate = async () => {
    setSavingExpenseTemplate(true);
    setError(null);
    setSuccess(null);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;

      const payload = {
        template_key: EXPENSE_PAYMENT_TEMPLATE_KEY,
        subject_template:
          expensePaymentTemplateDraft.subject_template.trim() || defaultExpensePaymentRequestSubject,
        body_template:
          expensePaymentTemplateDraft.body_template.trim() || defaultExpensePaymentRequestBody,
        is_active: true,
        updated_by: userId,
      };

      const { error: upsertError } = await supabase
        .from('expense_payment_email_templates')
        .upsert(payload, { onConflict: 'template_key' });

      if (upsertError) throw upsertError;

      await loadConfigurationData();
      setSuccess('Expense payment request email template saved.');
    } catch (err: any) {
      console.error('Error saving expense payment request template:', err);
      setError(err?.message || 'Failed to save expense payment request template');
    } finally {
      setSavingExpenseTemplate(false);
    }
  };

  const handleUpdateDefaultTaskTemplate = async (
    template: AdminTaskDefaultTemplate,
    patch: Partial<Pick<AdminTaskDefaultTemplate, 'assigned_to' | 'is_active'>>,
  ) => {
    setSavingDefaultTaskId(template.id);
    setError(null);
    setSuccess(null);
    try {
      const next: AdminTaskDefaultTemplate = { ...template, ...patch };
      setDefaultTaskTemplates((prev) =>
        prev.map((row) => (row.id === template.id ? next : row)),
      );

      const { error: updateError } = await supabase
        .from('admin_task_default_templates')
        .update({
          assigned_to: next.assigned_to,
          is_active: next.is_active,
        })
        .eq('id', template.id);

      if (updateError) throw updateError;

      setSuccess(`Updated "${template.title}".`);
    } catch (err: any) {
      console.error('Error saving default admin task template:', err);
      if (isMissingAdminTaskDefaultTemplatesTableError(err)) {
        setError(
          'Default admin task templates are not available yet. Run the latest database migrations and try again.',
        );
      } else {
        setError(err?.message || 'Failed to save default admin task template');
      }
      await loadConfigurationData();
    } finally {
      setSavingDefaultTaskId(null);
    }
  };

  const updateBillingProductDraft = (
    productId: string,
    patch: Partial<BillingProductDraft>,
  ) => {
    setBillingProducts((prev) =>
      prev.map((product) => (product.id === productId ? { ...product, ...patch } : product)),
    );
  };

  const updateBillingProductMetadata = (
    productId: string,
    key: string,
    value: string,
  ) => {
    setBillingProducts((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, metadata: { ...(product.metadata || {}), [key]: value } }
          : product,
      ),
    );
  };

  const setBillingProductAdminOnly = (productId: string, enabled: boolean) => {
    setBillingProducts((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, metadata: { ...(product.metadata || {}), admin_only: enabled } }
          : product,
      ),
    );
  };

  const toggleBillingProductExpanded = (productId: string) => {
    setExpandedBillingProductIds((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const toggleBillingProductMarket = (productId: string, marketId: string) => {
    setBillingProducts((prev) =>
      prev.map((product) => {
        if (product.id !== productId) return product;
        const selected = product.market_ids.includes(marketId);
        return {
          ...product,
          market_ids: selected
            ? product.market_ids.filter((id) => id !== marketId)
            : [...product.market_ids, marketId],
        };
      }),
    );
  };

  const handleSaveBillingProduct = async (product: BillingProductDraft) => {
    setSavingBillingProductId(product.id);
    setError(null);
    setSuccess(null);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;

      const { error: updateError } = await supabase
        .from('billing_products')
        .update({
          name: product.name.trim(),
          description: product.description?.trim() || null,
          stripe_product_id: product.stripe_product_id?.trim() || null,
          stripe_price_id: product.stripe_price_id?.trim() || null,
          stripe_metered_or_extra_price_id:
            product.stripe_metered_or_extra_price_id?.trim() || null,
          plan_value: product.plan_value?.trim() || null,
          amount_cents: Number.isFinite(product.amount_cents) ? product.amount_cents : 0,
          currency: (product.currency || 'usd').trim().toLowerCase(),
          interval: product.interval?.trim() || null,
          included_units: product.included_units,
          additional_unit_amount_cents: product.additional_unit_amount_cents,
          is_active: product.is_active,
          sort_order: Number.isFinite(product.sort_order) ? product.sort_order : 0,
          metadata: {
            ...(product.metadata || {}),
            admin_only: isAdminOnlyBillingProduct(product),
          },
          updated_by: userId,
        })
        .eq('id', product.id);

      if (updateError) throw updateError;

      if (markets.length > 0 && !isAdminOnlyBillingProduct(product)) {
        const availabilityPayload = markets.map((market) => ({
          billing_product_id: product.id,
          market_id: market.id,
          is_available: product.market_ids.includes(market.id),
          updated_by: userId,
        }));

        const { error: availabilityError } = await supabase
          .from('billing_product_markets')
          .upsert(availabilityPayload, { onConflict: 'billing_product_id,market_id' });

        if (availabilityError) throw availabilityError;
      }

      await loadConfigurationData();
      setSuccess(`Saved ${product.name}.`);
    } catch (err: any) {
      console.error('Error saving billing product:', err);
      if (isMissingBillingProductsTableError(err)) {
        setError('Billing products are not available yet. Run the latest database migrations and try again.');
      } else {
        setError(err?.message || 'Failed to save billing product');
      }
    } finally {
      setSavingBillingProductId(null);
    }
  };

  useEffect(() => {
    loadConfigurationData();
  }, []);

  const handleToggleVisitCalendarInvites = async () => {
    setSavingFlag(true);
    setError(null);
    setSuccess(null);
    try {
      const nextEnabled = !visitCalendarInvitesEnabled;
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;

      const { error: upsertError } = await supabase
        .from('app_feature_flags')
        .upsert(
          {
            key: 'visit_calendar_invites_enabled',
            is_enabled: nextEnabled,
            updated_by: userId,
          },
          { onConflict: 'key' }
        );

      if (upsertError) throw upsertError;

      setVisitCalendarInvitesEnabled(nextEnabled);
      setSuccess(nextEnabled ? 'Visit calendar invites enabled.' : 'Visit calendar invites disabled.');
    } catch (err: any) {
      console.error('Error updating visit calendar invite flag:', err);
      setError(err?.message || 'Failed to update feature flag');
    } finally {
      setSavingFlag(false);
    }
  };

  const handleSaveCalendarDescriptionTemplate = async (visitType: VisitType) => {
    setSavingVisitType(visitType);
    setError(null);
    setSuccess(null);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;
      const draft = drafts[visitType];

      const payload = {
        visit_type: visitType,
        body_template: draft.body_template.trim(),
        is_active: true,
        updated_by: userId,
      };

      const { error: upsertError } = await supabase
        .from('visit_calendar_description_templates')
        .upsert(payload, { onConflict: 'visit_type' });

      if (upsertError) throw upsertError;

      await loadConfigurationData();
      setSuccess(`${capitalize(visitType)} calendar description template saved.`);
    } catch (err: any) {
      console.error('Error saving visit calendar description template:', err);
      setError(err?.message || 'Failed to save template');
    } finally {
      setSavingVisitType(null);
    }
  };

  const tokenHint = useMemo(
    () => 'Available variables: {{visitType}}, {{scheduledDate}}, {{address}}, {{appUrl}}',
    [],
  );
  const expenseTokenHint = useMemo(
    () =>
      'Available variables: {{homeownerName}}, {{amount}}, {{serviceRequestTitle}}, {{serviceRequestId}}, {{expenseDescription}}, {{expenseDescriptionSection}}, {{lineItemsSection}}, {{appUrl}}',
    [],
  );

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.pageHeader}>
        <Settings size={20} color="#374151" />
        <Text style={styles.pageTitle}>Configuration</Text>
      </View>

      {error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      {success ? (
        <View style={styles.successContainer}>
          <Text style={styles.successText}>{success}</Text>
        </View>
      ) : null}

      <View style={styles.card}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() => setEmailSectionExpanded((prev) => !prev)}
        >
          <Text style={styles.sectionTitle}>Email Templates</Text>
          {emailSectionExpanded ? (
            <ChevronUp size={16} color="#6b7280" />
          ) : (
            <ChevronDown size={16} color="#6b7280" />
          )}
        </Pressable>

        {emailSectionExpanded ? (
          <>
            <View style={styles.subsection}>
              <Text style={styles.subsectionTitle}>Expense Payment Request Email</Text>
              <Text style={styles.sectionDescription}>
                Configure the email sent when admins request homeowner payment for an expense.
              </Text>
              <Text style={styles.tokenHint}>{expenseTokenHint}</Text>

              <View style={styles.templateCard}>
                <Text style={styles.inputLabel}>Subject template</Text>
                <TextInput
                  style={styles.input}
                  value={expensePaymentTemplateDraft.subject_template}
                  onChangeText={(value) =>
                    setExpensePaymentTemplateDraft((prev) => ({
                      ...prev,
                      subject_template: value,
                    }))
                  }
                  placeholder={defaultExpensePaymentRequestSubject}
                  placeholderTextColor="#9ca3af"
                />

                <Text style={styles.inputLabel}>Body template</Text>
                <TextInput
                  style={[styles.input, styles.bodyInput]}
                  multiline
                  textAlignVertical="top"
                  value={expensePaymentTemplateDraft.body_template}
                  onChangeText={(value) =>
                    setExpensePaymentTemplateDraft((prev) => ({
                      ...prev,
                      body_template: value,
                    }))
                  }
                  placeholder={defaultExpensePaymentRequestBody}
                  placeholderTextColor="#9ca3af"
                />

                <Pressable
                  style={[styles.saveButton, savingExpenseTemplate && styles.saveButtonDisabled]}
                  onPress={handleSaveExpensePaymentTemplate}
                  disabled={savingExpenseTemplate}
                >
                  {savingExpenseTemplate ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <>
                      <Check size={14} color="#ffffff" />
                      <Text style={styles.saveButtonText}>Save payment request template</Text>
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          </>
        ) : null}
      </View>

      <View style={styles.card}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() => setCalendarSettingsExpanded((prev) => !prev)}
        >
          <Text style={styles.sectionTitle}>Calendar Settings</Text>
          {calendarSettingsExpanded ? (
            <ChevronUp size={16} color="#6b7280" />
          ) : (
            <ChevronDown size={16} color="#6b7280" />
          )}
        </Pressable>

        {calendarSettingsExpanded ? (
          <View style={styles.subsection}>
            <Text style={styles.subsectionTitle}>Visit Calendar Invites</Text>
            <Text style={styles.sectionDescription}>
              Toggle automatic create/update/cancel for visit invites in Google Calendar.
            </Text>
            <Pressable
              style={[
                styles.toggleButton,
                visitCalendarInvitesEnabled ? styles.toggleEnabled : styles.toggleDisabled,
                savingFlag && styles.toggleDisabledState,
              ]}
              onPress={handleToggleVisitCalendarInvites}
              disabled={savingFlag || loading}
            >
              {savingFlag ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={styles.toggleButtonText}>
                  {visitCalendarInvitesEnabled ? 'Enabled' : 'Disabled'}
                </Text>
              )}
            </Pressable>

            <View style={styles.templateCard}>
              <Text style={styles.subsectionTitle}>Default Visit Durations</Text>
              <Text style={styles.sectionDescription}>
                Used to prefill duration when admins schedule visits by type.
              </Text>
              {VISIT_TYPES.map((visitType) => (
                <View key={visitType} style={styles.durationRow}>
                  <Text style={styles.durationLabel}>{capitalize(visitType)}</Text>
                  <View style={styles.durationOptionsContainer}>
                    {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => {
                      const selected = visitDurationDefaults[visitType] === minutes;
                      return (
                        <Pressable
                          key={`${visitType}-${minutes}`}
                          onPress={() =>
                            setVisitDurationDefaults((prev) => ({
                              ...prev,
                              [visitType]: minutes,
                            }))
                          }
                          style={[
                            styles.durationOptionChip,
                            selected && styles.durationOptionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              styles.durationOptionChipText,
                              selected && styles.durationOptionChipTextSelected,
                            ]}
                          >
                            {formatVisitDurationLabel(minutes)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ))}
              <Pressable
                style={[
                  styles.saveButton,
                  savingVisitDurationDefaults && styles.saveButtonDisabled,
                ]}
                onPress={handleSaveVisitDurationDefaults}
                disabled={savingVisitDurationDefaults}
              >
                {savingVisitDurationDefaults ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <>
                    <Check size={14} color="#ffffff" />
                    <Text style={styles.saveButtonText}>Save default durations</Text>
                  </>
                )}
              </Pressable>
            </View>
          </View>
        ) : null}
      </View>

      <View style={styles.card}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() => setCalendarTemplatesExpanded((prev) => !prev)}
        >
          <Text style={styles.sectionTitle}>Calendar Event Descriptions</Text>
          {calendarTemplatesExpanded ? (
            <ChevronUp size={16} color="#6b7280" />
          ) : (
            <ChevronDown size={16} color="#6b7280" />
          )}
        </Pressable>

        {calendarTemplatesExpanded ? (
          <View style={styles.subsection}>
            <Text style={styles.subsectionTitle}>Visit Calendar Description Templates</Text>
            <Text style={styles.tokenHint}>{tokenHint}</Text>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#6b7280" />
                <Text style={styles.loadingText}>Loading templates...</Text>
              </View>
            ) : (
              VISIT_TYPES.map((visitType) => (
                <View key={visitType} style={styles.templateCard}>
                  <Text style={styles.templateTitle}>{capitalize(visitType)}</Text>

                  <Text style={styles.inputLabel}>Body template</Text>
                  <TextInput
                    style={[styles.input, styles.bodyInput]}
                    multiline
                    textAlignVertical="top"
                    value={drafts[visitType].body_template}
                    onChangeText={(value) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [visitType]: { ...prev[visitType], body_template: value },
                      }))
                    }
                  />

                  <Pressable
                    style={[styles.saveButton, savingVisitType === visitType && styles.saveButtonDisabled]}
                    onPress={() => handleSaveCalendarDescriptionTemplate(visitType)}
                    disabled={savingVisitType === visitType}
                  >
                    {savingVisitType === visitType ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <>
                        <Check size={14} color="#ffffff" />
                        <Text style={styles.saveButtonText}>
                          Save {capitalize(visitType)} description
                        </Text>
                      </>
                    )}
                  </Pressable>
                </View>
              ))
            )}
          </View>
        ) : null}
      </View>

      <View style={styles.card}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() => setBillingProductsExpanded((prev) => !prev)}
        >
          <Text style={styles.sectionTitle}>Billing Products</Text>
          {billingProductsExpanded ? (
            <ChevronUp size={16} color="#6b7280" />
          ) : (
            <ChevronDown size={16} color="#6b7280" />
          )}
        </Pressable>

        {billingProductsExpanded ? (
          <View style={styles.subsection}>
            <Text style={styles.sectionDescription}>
              Configure Stripe products and prices for plan tiers and add-ons. Paid premium is
              shown on the homeowner profile. Complimentary premium is admin-only: paste your
              free annual Stripe price ID, then assign it to customers from their admin profile.
            </Text>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#6b7280" />
                <Text style={styles.loadingText}>Loading billing products...</Text>
              </View>
            ) : billingProducts.length === 0 ? (
              <Text style={styles.placeholderText}>
                No billing products found. Run the latest database migrations to seed the
                billing catalog.
              </Text>
            ) : (
              sortBillingProducts(billingProducts).map((product) => {
                const isSaving = savingBillingProductId === product.id;
                const isAdminOnly = isAdminOnlyBillingProduct(product);
                const isProductExpanded = expandedBillingProductIds[product.id] === true;
                return (
                  <View key={product.id} style={styles.templateCard}>
                    <View style={styles.billingProductHeaderRow}>
                      <Pressable
                        style={styles.billingProductHeaderButton}
                        onPress={() => toggleBillingProductExpanded(product.id)}
                      >
                        <View style={styles.billingProductHeaderText}>
                          <Text style={styles.templateTitle}>{product.name}</Text>
                          <Text style={styles.tokenHint}>
                            {product.slug} • {product.kind.replace('_', ' ')} • {product.billing_mode}
                            {product.plan_value ? ` • plan: ${product.plan_value}` : ''}
                          </Text>
                          {!isProductExpanded ? (
                            <Text style={styles.billingProductSummary}>
                              {product.stripe_price_id
                                ? `Stripe price: ${product.stripe_price_id}`
                                : 'Stripe price not configured'}
                              {isAdminOnly ? ' • Admin only' : ''}
                            </Text>
                          ) : isAdminOnly ? (
                            <Text style={styles.adminOnlyHint}>
                              Admin only — hidden from homeowner profile and self-serve checkout.
                            </Text>
                          ) : null}
                        </View>
                        {isProductExpanded ? (
                          <ChevronUp size={16} color="#6b7280" />
                        ) : (
                          <ChevronDown size={16} color="#6b7280" />
                        )}
                      </Pressable>
                      <Pressable
                        style={[
                          styles.toggleChip,
                          product.is_active ? styles.toggleChipOn : styles.toggleChipOff,
                          isSaving && styles.saveButtonDisabled,
                        ]}
                        onPress={() =>
                          updateBillingProductDraft(product.id, { is_active: !product.is_active })
                        }
                        disabled={isSaving}
                      >
                        <Text
                          style={[
                            styles.toggleChipText,
                            product.is_active
                              ? styles.toggleChipTextOn
                              : styles.toggleChipTextOff,
                          ]}
                        >
                          {product.is_active ? 'Active' : 'Disabled'}
                        </Text>
                      </Pressable>
                    </View>

                    {isProductExpanded ? (
                      <View style={styles.billingProductBody}>
                    {product.kind === 'base_plan' ? (
                      <>
                        <Text style={styles.inputLabel}>Plan value</Text>
                        <TextInput
                          style={styles.input}
                          value={product.plan_value || ''}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, { plan_value: value })
                          }
                          placeholder="managed"
                          placeholderTextColor="#9ca3af"
                          autoCapitalize="none"
                        />
                        <Pressable
                          style={[
                            styles.toggleChip,
                            isAdminOnly ? styles.toggleChipOn : styles.toggleChipOff,
                            isSaving && styles.saveButtonDisabled,
                            styles.adminOnlyToggle,
                          ]}
                          onPress={() => setBillingProductAdminOnly(product.id, !isAdminOnly)}
                          disabled={isSaving}
                        >
                          <Text
                            style={[
                              styles.toggleChipText,
                              isAdminOnly ? styles.toggleChipTextOn : styles.toggleChipTextOff,
                            ]}
                          >
                            {isAdminOnly ? 'Admin-only tier' : 'Homeowner self-serve'}
                          </Text>
                        </Pressable>
                      </>
                    ) : null}

                    <Text style={styles.inputLabel}>Display name</Text>
                    <TextInput
                      style={styles.input}
                      value={product.name}
                      onChangeText={(value) => updateBillingProductDraft(product.id, { name: value })}
                      placeholder="Product name"
                      placeholderTextColor="#9ca3af"
                    />

                    <Text style={styles.inputLabel}>Description</Text>
                    <TextInput
                      style={[styles.input, styles.bodyInputSmall]}
                      multiline
                      textAlignVertical="top"
                      value={product.description || ''}
                      onChangeText={(value) =>
                        updateBillingProductDraft(product.id, { description: value })
                      }
                      placeholder="Describe what the homeowner is buying"
                      placeholderTextColor="#9ca3af"
                    />

                    <View style={styles.billingFieldsGrid}>
                      <View style={styles.billingField}>
                        <Text style={styles.inputLabel}>Stripe product ID</Text>
                        <TextInput
                          style={styles.input}
                          value={product.stripe_product_id || ''}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, { stripe_product_id: value })
                          }
                          placeholder="prod_..."
                          placeholderTextColor="#9ca3af"
                          autoCapitalize="none"
                        />
                      </View>
                      <View style={styles.billingField}>
                        <Text style={styles.inputLabel}>Stripe price ID</Text>
                        <TextInput
                          style={styles.input}
                          value={product.stripe_price_id || ''}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, { stripe_price_id: value })
                          }
                          placeholder="price_..."
                          placeholderTextColor="#9ca3af"
                          autoCapitalize="none"
                        />
                      </View>
                    </View>

                    {!isAdminOnly ? (
                      <View style={styles.billingFieldsGrid}>
                        <View style={styles.billingField}>
                          <Text style={styles.inputLabel}>Display price</Text>
                          <TextInput
                            style={styles.input}
                            value={typeof product.metadata?.display_price === 'string'
                              ? product.metadata.display_price
                              : ''}
                            onChangeText={(value) =>
                              updateBillingProductMetadata(product.id, 'display_price', value)
                            }
                            placeholder="$99 per visit"
                            placeholderTextColor="#9ca3af"
                          />
                        </View>
                        <View style={styles.billingField}>
                          <Text style={styles.inputLabel}>CTA label</Text>
                          <TextInput
                            style={styles.input}
                            value={typeof product.metadata?.cta === 'string' ? product.metadata.cta : ''}
                            onChangeText={(value) =>
                              updateBillingProductMetadata(product.id, 'cta', value)
                            }
                            placeholder="Select"
                            placeholderTextColor="#9ca3af"
                          />
                        </View>
                      </View>
                    ) : (
                      <Text style={styles.placeholderText}>
                        Complimentary tiers use the Stripe price above when admins grant premium to
                        a customer. No market or homeowner checkout configuration is required.
                      </Text>
                    )}

                    <View style={styles.billingFieldsGrid}>
                      <View style={styles.billingField}>
                        <Text style={styles.inputLabel}>Amount cents</Text>
                        <TextInput
                          style={styles.input}
                          value={String(product.amount_cents ?? 0)}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, {
                              amount_cents: Number(value.replace(/[^\d]/g, '') || 0),
                            })
                          }
                          keyboardType="number-pad"
                        />
                      </View>
                      <View style={styles.billingField}>
                        <Text style={styles.inputLabel}>Sort order</Text>
                        <TextInput
                          style={styles.input}
                          value={String(product.sort_order ?? 0)}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, {
                              sort_order: Number(value.replace(/[^\d-]/g, '') || 0),
                            })
                          }
                          keyboardType="number-pad"
                        />
                      </View>
                    </View>

                    <View style={styles.billingFieldsGrid}>
                      <View style={styles.billingField}>
                        <Text style={styles.inputLabel}>Included units</Text>
                        <TextInput
                          style={styles.input}
                          value={product.included_units === null ? '' : String(product.included_units)}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, {
                              included_units: value ? Number(value.replace(/[^\d]/g, '')) : null,
                            })
                          }
                          keyboardType="number-pad"
                        />
                      </View>
                      <View style={styles.billingField}>
                        <Text style={styles.inputLabel}>Additional unit cents</Text>
                        <TextInput
                          style={styles.input}
                          value={product.additional_unit_amount_cents === null
                            ? ''
                            : String(product.additional_unit_amount_cents)}
                          onChangeText={(value) =>
                            updateBillingProductDraft(product.id, {
                              additional_unit_amount_cents: value
                                ? Number(value.replace(/[^\d]/g, ''))
                                : null,
                            })
                          }
                          keyboardType="number-pad"
                        />
                      </View>
                    </View>

                    {!isAdminOnly ? (
                      <>
                        <Text style={styles.inputLabel}>Available markets</Text>
                        {markets.length === 0 ? (
                          <Text style={styles.placeholderText}>No active markets found.</Text>
                        ) : (
                          <View style={styles.durationOptionsContainer}>
                            {markets.map((market) => {
                              const selected = product.market_ids.includes(market.id);
                              return (
                                <Pressable
                                  key={`${product.id}-${market.id}`}
                                  onPress={() => toggleBillingProductMarket(product.id, market.id)}
                                  style={[
                                    styles.durationOptionChip,
                                    selected && styles.durationOptionChipSelected,
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.durationOptionChipText,
                                      selected && styles.durationOptionChipTextSelected,
                                    ]}
                                  >
                                    {market.name}
                                  </Text>
                                </Pressable>
                              );
                            })}
                          </View>
                        )}
                      </>
                    ) : null}

                    <Pressable
                      style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                      onPress={() => handleSaveBillingProduct(product)}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <>
                          <Check size={14} color="#ffffff" />
                          <Text style={styles.saveButtonText}>Save billing product</Text>
                        </>
                      )}
                    </Pressable>
                      </View>
                    ) : null}
                  </View>
                );
              })
            )}
          </View>
        ) : null}
      </View>

      <View style={styles.card}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() => setDefaultTasksExpanded((prev) => !prev)}
        >
          <Text style={styles.sectionTitle}>Default Admin Tasks for New Users</Text>
          {defaultTasksExpanded ? (
            <ChevronUp size={16} color="#6b7280" />
          ) : (
            <ChevronDown size={16} color="#6b7280" />
          )}
        </Pressable>

        {defaultTasksExpanded ? (
          <View style={styles.subsection}>
            <Text style={styles.sectionDescription}>
              These tasks are automatically created when a new homeowner signs up. Pick an
              assignee to activate a task; choose Unassigned to skip it.
            </Text>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color="#6b7280" />
                <Text style={styles.loadingText}>Loading default tasks...</Text>
              </View>
            ) : defaultTaskTemplates.length === 0 ? (
              <Text style={styles.placeholderText}>
                No default task templates found. Run the latest database migrations to seed
                them.
              </Text>
            ) : (
              defaultTaskTemplates.map((template) => {
                const isSaving = savingDefaultTaskId === template.id;
                return (
                  <View key={template.id} style={styles.templateCard}>
                    <View style={styles.defaultTaskHeader}>
                      <Text style={styles.templateTitle}>{template.title}</Text>
                      <Pressable
                        style={[
                          styles.toggleChip,
                          template.is_active ? styles.toggleChipOn : styles.toggleChipOff,
                          isSaving && styles.saveButtonDisabled,
                        ]}
                        onPress={() =>
                          handleUpdateDefaultTaskTemplate(template, {
                            is_active: !template.is_active,
                          })
                        }
                        disabled={isSaving}
                      >
                        <Text
                          style={[
                            styles.toggleChipText,
                            template.is_active
                              ? styles.toggleChipTextOn
                              : styles.toggleChipTextOff,
                          ]}
                        >
                          {template.is_active ? 'Active' : 'Disabled'}
                        </Text>
                      </Pressable>
                    </View>

                    {template.description ? (
                      <Text style={styles.sectionDescription}>{template.description}</Text>
                    ) : null}

                    <Text style={styles.inputLabel}>Assignee</Text>
                    <View style={styles.durationOptionsContainer}>
                      <Pressable
                        onPress={() =>
                          handleUpdateDefaultTaskTemplate(template, { assigned_to: null })
                        }
                        disabled={isSaving}
                        style={[
                          styles.durationOptionChip,
                          template.assigned_to === null &&
                            styles.durationOptionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            styles.durationOptionChipText,
                            template.assigned_to === null &&
                              styles.durationOptionChipTextSelected,
                          ]}
                        >
                          Unassigned
                        </Text>
                      </Pressable>
                      {systemManagerOptions.map((mgr) => {
                        const selected = template.assigned_to === mgr.id;
                        return (
                          <Pressable
                            key={mgr.id}
                            onPress={() =>
                              handleUpdateDefaultTaskTemplate(template, {
                                assigned_to: mgr.id,
                              })
                            }
                            disabled={isSaving}
                            style={[
                              styles.durationOptionChip,
                              selected && styles.durationOptionChipSelected,
                            ]}
                          >
                            <Text
                              style={[
                                styles.durationOptionChipText,
                                selected && styles.durationOptionChipTextSelected,
                              ]}
                            >
                              {mgr.full_name || 'Unnamed manager'}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>

                    {isSaving ? (
                      <View style={styles.loadingContainer}>
                        <ActivityIndicator size="small" color="#6b7280" />
                        <Text style={styles.loadingText}>Saving...</Text>
                      </View>
                    ) : null}
                  </View>
                );
              })
            )}
          </View>
        ) : null}
      </View>
    </WebScrollView>
  );
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    gap: 12,
  },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
  },
  card: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    padding: 16,
    ...shadows.sm,
  },
  sectionHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  sectionDescription: {
    marginTop: 6,
    color: '#4b5563',
    fontSize: 14,
  },
  subsection: {
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    marginTop: 12,
    paddingTop: 12,
    gap: 12,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  tokenHint: {
    fontSize: 12,
    color: '#6b7280',
  },
  placeholderText: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  toggleButton: {
    marginTop: 4,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignSelf: 'flex-start',
    minWidth: 110,
    alignItems: 'center',
  },
  toggleEnabled: {
    backgroundColor: '#166534',
  },
  toggleDisabled: {
    backgroundColor: '#991b1b',
  },
  toggleDisabledState: {
    opacity: 0.7,
  },
  toggleButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
  },
  durationRow: {
    gap: 6,
    paddingTop: 4,
  },
  durationLabel: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '600',
  },
  durationOptionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  durationOptionChip: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#ffffff',
  },
  durationOptionChipSelected: {
    backgroundColor: '#111827',
    borderColor: '#111827',
  },
  durationOptionChipText: {
    color: '#374151',
    fontSize: 12,
    fontWeight: '600',
  },
  durationOptionChipTextSelected: {
    color: '#ffffff',
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  errorText: {
    color: '#b91c1c',
    fontSize: 13,
  },
  successContainer: {
    backgroundColor: '#dcfce7',
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  successText: {
    color: '#166534',
    fontSize: 13,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    color: '#6b7280',
    fontSize: 14,
  },
  templateCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 12,
    gap: 8,
  },
  templateTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  inputLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  bodyInput: {
    minHeight: 110,
  },
  bodyInputSmall: {
    minHeight: 72,
  },
  billingFieldsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  billingField: {
    flexGrow: 1,
    flexBasis: 240,
    gap: 6,
  },
  billingProductHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  billingProductHeaderButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  billingProductHeaderText: {
    flex: 1,
    gap: 2,
  },
  billingProductSummary: {
    marginTop: 4,
    fontSize: 12,
    color: '#4b5563',
    lineHeight: 16,
  },
  billingProductBody: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    gap: 10,
  },
  adminOnlyHint: {
    marginTop: 4,
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 16,
  },
  adminOnlyToggle: {
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  saveButton: {
    marginTop: 4,
    backgroundColor: '#111827',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
  },
  saveButtonDisabled: {
    opacity: 0.7,
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  defaultTaskHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  toggleChip: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1,
  },
  toggleChipOn: {
    backgroundColor: '#dcfce7',
    borderColor: '#86efac',
  },
  toggleChipOff: {
    backgroundColor: '#f3f4f6',
    borderColor: '#d1d5db',
  },
  toggleChipText: {
    fontSize: 12,
    fontWeight: '700',
  },
  toggleChipTextOn: {
    color: '#166534',
  },
  toggleChipTextOff: {
    color: '#6b7280',
  },
});

export default ConfigurationTab;
